import { ApiClient } from '@/services/shared/ApiClient';
import type { ResponseInterceptor } from '@/services/shared/ApiClient';
import { useMfaStore, MFA_REDIRECT_PATH } from '@/stores/mfaStore';

const MFA_REQUIRED_HEADER = 'x-mfa-required';

let interceptorsRegistered = false;

/**
 * MFA Response Interceptor
 *
 * Handles 403 responses with X-MFA-Required header by:
 * 1. Setting MFA required state in the store
 * 2. Triggering redirect to MFA page (only once)
 *
 * The response is still returned to allow the ApiClient to handle
 * the error properly, but the MFA redirect is triggered immediately.
 */
export const mfaResponseInterceptor: ResponseInterceptor = async (response) => {
  const headerValue = response.headers.get(MFA_REQUIRED_HEADER);
  const mfaRequired = response.status === 403 && headerValue?.toLowerCase() === 'true';

  if (mfaRequired) {
    try {
      const mfaStore = useMfaStore.getState();

      // Set MFA required state
      mfaStore.setMfaRequired(true);

      // Trigger redirect if not already done
      // This returns false if already redirected or on MFA page
      mfaStore.triggerMfaRedirect();
    } catch (error) {
      // Best effort: do not break the response chain if Zustand store is unavailable
      console.warn('Failed to handle MFA required state', error);
    }
  }

  return response;
};

export const registerHttpInterceptors = (): void => {
  if (interceptorsRegistered) {
    return;
  }

  ApiClient.addGlobalResponseInterceptor(mfaResponseInterceptor);
  interceptorsRegistered = true;
};

export const resetHttpInterceptors = (): void => {
  ApiClient.clearGlobalInterceptors();
  interceptorsRegistered = false;
};

// Re-export for convenience
export { MFA_REDIRECT_PATH };

